/**
 * Standalone headline stat (Schedule row — §12.5 rule 2).
 *
 * Like `<Num>`, it takes envelopes only: label text comes from the caller and every number comes
 * from the server as `Explained`. The headline figure's tooltip is lifted out of the number and
 * into the card's top-right corner, the one (i) placement every stat island shares.
 */

import type { ReactNode } from 'react';
import type { Explained } from '@lw/shared';
import { Num } from './Num';
import { TooltipPopover } from './TooltipPopover';
import { cx } from './cx';

export type StatCardProps = {
  label: string;
  value: Explained<unknown>;
  /** Optional secondary figure, e.g. the median beneath the average. */
  secondary?: Explained<unknown>;
  secondaryLabel?: string;
  footer?: ReactNode;
  className?: string;
};

export function StatCard({
  label,
  value,
  secondary,
  secondaryLabel,
  footer,
  className,
}: StatCardProps) {
  return (
    <div className={cx('stat-card', className)}>
      <div className="stat-card__label">
        <span>{label}</span>
      </div>
      <Num className="stat-card__value" value={value} withTooltip={false} />
      {secondary ? (
        <div className="stat-card__sub">
          {secondaryLabel ? <span>{secondaryLabel} </span> : null}
          <Num value={secondary} tooltipLabel={secondaryLabel} />
        </div>
      ) : null}
      {footer ? <div className="stat-card__sub">{footer}</div> : null}
      {value.tooltip !== undefined ? (
        <TooltipPopover
          tooltip={value.tooltip}
          align="left"
          label={`How ${label} is calculated`}
        />
      ) : null}
    </div>
  );
}

export default StatCard;
